import { useEffect, useState } from "react";
import { FilesDocDTO, NodoDocDTO, SomacylEntregablesDTO, SomacylOrderENTITY, StateInternalOrderSomacyl } from "sigo-entities";
import { AdjuntosV1 } from "../../shared/Components/AdjuntosV1/Adjuntos";
import { Row } from "react-bootstrap";
import { AdapterGenerico } from "../../shared/Infraestructure/AdapterGenerico";
import { useSelector } from "react-redux";
import { RootState } from "../../shared/Infraestructure/AdapterStore";

interface Props {
    row: SomacylOrderENTITY
    uploadPhotosSomacylOrders: (entregables: SomacylEntregablesDTO, id: string, files: File[]) => Promise<void>
    isLoading: boolean
}

const CACHE_KEY = (id: string) => `somacyl_photos_${id}`

function initNodos(row: SomacylOrderENTITY): NodoDocDTO[] {
    try {
        const cached = sessionStorage.getItem(CACHE_KEY(row._id))
        if (cached) return JSON.parse(cached)
    } catch { }
    const Extension = ['jpeg', 'bmp', 'png', 'jpg', 'ALL']
    const Size = { Size: 50, UM: 'mb' }
    const Estado = { ID_Estado: 1, Nombre: 'ACTIVO' }
    const docs: NodoDocDTO[] = [
        { Titulo: 'FOTO ANTES', Codigo: 'Cod1', Descripcion: 'FOTO ANTES', Files: [], Extension, Size, Requerido: true, CantidadMin: 1, CantidadMax: 1, Children: [], Estado, label: 'FOTO ANTES', id: 1 },
        { Titulo: 'FOTO DESPUES', Codigo: 'Cod2', Descripcion: 'FOTO DESPUES', Files: [], Extension, Size, Requerido: true, CantidadMin: 1, CantidadMax: 1, Children: [], Estado, label: 'FOTO DESPUES', id: 2 },
        { Titulo: 'CONJUNTO DE MEDIDA ANTES', Codigo: 'Cod3', Descripcion: 'CONJUNTO DE MEDIDA ANTES', Files: [], Extension, Size, Requerido: true, CantidadMin: 1, CantidadMax: 1, Children: [], Estado, label: 'CONJUNTO DE MEDIDA ANTES', id: 3 },
        { Titulo: 'CONJUNTO DE MEDIDA DESPUES', Codigo: 'Cod4', Descripcion: 'CONJUNTO DE MEDIDA DESPUES', Files: [], Extension, Size, Requerido: true, CantidadMin: 1, CantidadMax: 1, Children: [], Estado, label: 'CONJUNTO DE MEDIDA DESPUES', id: 4 }
    ]
    return docs
}

const REQUIRED_CODES = ['Cod1', 'Cod2', 'Cod3', 'Cod4'] as const
const CODE_LABEL: Record<typeof REQUIRED_CODES[number], string> = {
    Cod1: 'FOTO ANTES',
    Cod2: 'FOTO DESPUES',
    Cod3: 'CONJUNTO DE MEDIDA ANTES',
    Cod4: 'CONJUNTO DE MEDIDA DESPUES'
}

export function Photos({ row, uploadPhotosSomacylOrders, isLoading }: Props) {
    const [nodos, setNodos] = useState<NodoDocDTO[]>(() => initNodos(row))
    const [newFiles, setNewFiles] = useState<File[]>([])
    const [inicializado, setInicializado] = useState(false)
    const { user } = useSelector((state: RootState) => state.auth)
    const ruta = `ORDENES_SOMACYL_${row._id.replaceAll('-', '').toUpperCase()}_PHOTOS_`
    // Mismo cálculo que AccordionAdjuntos: es el prefijo real con el que se nombra
    // el File subido, usado aquí para verificar que el binario exista de verdad.
    const paisDelegacion = `${user.Pais.Nombre}_${user.Delegacion.Codigo}`
        .replaceAll('Ñ', 'N')
        .replaceAll('ñ', 'n')

    const wrappedSetNodos = (nuevoNodos: NodoDocDTO[] | ((prev: NodoDocDTO[]) => NodoDocDTO[])) => {
        setNodos(prev => {
            const next = typeof nuevoNodos === 'function' ? nuevoNodos(prev) : nuevoNodos
            return next === prev ? [...next] : next
        })
    }

    const restoreFiles = (documentacion: NodoDocDTO[]) => {
        for (const nodo of documentacion) {
            const files = [
                row.entregables.foto_antes,
                row.entregables.foto_despues,
                row.entregables.conjunto_medida_antes,
                row.entregables.conjunto_medida_despues
            ].filter(e => e.CodigoNodo === nodo.Codigo)
            nodo.Files = files
            restoreFiles(nodo.Children)
        }
    }

    useEffect(() => {
        if (!row || inicializado) return
        const cached = sessionStorage.getItem(CACHE_KEY(row._id))
        if (!cached) {
            restoreFiles(nodos)
            wrappedSetNodos([...nodos])
        }
        setInicializado(true)
    }, [row])

    useEffect(() => {
        if (!inicializado) return
        const serializable = nodos.map(n => ({ ...n, Files: n.Files.map(f => ({ ...f, File: undefined })) }))
        sessionStorage.setItem(CACHE_KEY(row._id), JSON.stringify(serializable))
    }, [nodos])

    // Nombre con el que AccordionAdjuntos sube/renombra el binario real (ver onAdd).
    // Si un archivo "nuevo" no aparece con este nombre exacto en newFiles, es que
    // el binario no está disponible (p.ej. se restauró el nodo desde sessionStorage
    // tras un refresh, o el adjunto nunca llegó a cargarse) y no debe grabarse.
    const buildExpectedFileName = (archivo: FilesDocDTO) => `${paisDelegacion}_${archivo.Ruta}-${archivo.Filename}`

    const savePhotos = async () => {
        try {
            if (row.estado_interno === StateInternalOrderSomacyl.FINALIZADA || row.estado_interno === StateInternalOrderSomacyl.NO_FINALIZADA) {
                AdapterGenerico.createMessage('Alerta', 'No se pueden modificar las fotos de una orden finalizada', 'warning')
                return
            }
            const entregables = structuredClone(row.entregables)
            entregables.latitud = Number(entregables.latitud) || 0
            entregables.longitud = Number(entregables.longitud) || 0

            const previousPorCodigo: Record<typeof REQUIRED_CODES[number], FilesDocDTO> = {
                Cod1: row.entregables.foto_antes,
                Cod2: row.entregables.foto_despues,
                Cod3: row.entregables.conjunto_medida_antes,
                Cod4: row.entregables.conjunto_medida_despues
            }

            // No se exigen las 4 fotos aquí: según cómo termine la orden
            // (FinalizeOrder) puede bastar con "foto antes" (NO_FINALIZADA) o
            // requerirse las 4 (FINALIZADA). Esta pantalla permite guardar lo que
            // el técnico ya tenga, y FinalizeOrder exige lo que corresponda según
            // el estado elegido antes de dejar finalizar.
            const bloqueado: string[] = []
            const filesToSend: File[] = []
            let hayCambios = false

            for (const codigo of REQUIRED_CODES) {
                const nodo = nodos.find((n) => n.Codigo === codigo)
                const archivoActivo = nodo?.Files.find((f) => f.Estado?.ID_Estado === 1)
                const previo = previousPorCodigo[codigo]

                if (!archivoActivo || !archivoActivo.Filename) {
                    // Slot sin foto activa: si antes tenía una y se quitó, se
                    // limpia explícitamente; si nunca tuvo, no hay nada que hacer.
                    if (previo?.Filename) {
                        if (codigo === 'Cod1') entregables.foto_antes = new FilesDocDTO()
                        if (codigo === 'Cod2') entregables.foto_despues = new FilesDocDTO()
                        if (codigo === 'Cod3') entregables.conjunto_medida_antes = new FilesDocDTO()
                        if (codigo === 'Cod4') entregables.conjunto_medida_despues = new FilesDocDTO()
                        hayCambios = true
                    }
                    continue
                }

                const esNueva = archivoActivo.Filename !== previo?.Filename
                if (esNueva) {
                    const expectedName = buildExpectedFileName(archivoActivo)
                    const file = newFiles.find((f) => f.name === expectedName)
                    // Si cambió el adjunto pero su binario no está en newFiles,
                    // no hay nada real que subir: se bloquea esa foto en vez de
                    // grabar un Filename huérfano en Mongo.
                    if (!file) {
                        bloqueado.push(`${CODE_LABEL[codigo]} (archivo no disponible, vuelva a adjuntarlo)`)
                        continue
                    }
                    filesToSend.push(file)
                    hayCambios = true
                }

                if (codigo === 'Cod1') entregables.foto_antes = archivoActivo
                if (codigo === 'Cod2') entregables.foto_despues = archivoActivo
                if (codigo === 'Cod3') entregables.conjunto_medida_antes = archivoActivo
                if (codigo === 'Cod4') entregables.conjunto_medida_despues = archivoActivo
            }

            if (bloqueado.length) {
                AdapterGenerico.createMessage(
                    'Alerta',
                    `No se pudo guardar: ${bloqueado.join(', ')}`,
                    'warning'
                )
                return
            }

            if (!hayCambios) return

            // 2) Solo se graba la entidad si TODOS los archivos nuevos terminaron
            // de subirse; uploadPhotosSomacylOrders sube primero y recién luego
            // persiste la entidad, por lo que un fallo de red en cualquier
            // archivo interrumpe el flujo antes de tocar Mongo.
            await uploadPhotosSomacylOrders(entregables, row._id, filesToSend)
            AdapterGenerico.createToast('Adjuntos guardados correctamente', 'success')
            sessionStorage.removeItem(CACHE_KEY(row._id))
        } catch (error) {
            AdapterGenerico.createMessage('Alerta', (error as Error).message, 'error')
        }
    }

    return (
        <Row className='mt-4'>
            <AdjuntosV1
                Nodos={nodos}
                setNodos={wrappedSetNodos}
                ruta={ruta}
                newFiles={newFiles}
                setNewFiles={setNewFiles}
                onSave={savePhotos}
                loading={isLoading}
                validateMetadata={true}
            />
        </Row>
    )
}